import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/form/Checkbox';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { ORGANIZATION_DISPLAY_NAME_KEY } from '@/hooks/profilePicture';
import { useMy } from '@/hooks/useMy';
import type { ExportFormatType } from '@/hooks/useReportExport';
import {
  ExportFormat,
  useExportReport,
  useValidateReport,
  ValidateReportError,
} from '@/hooks/useReportExport';
import { useUpdateSetting } from '@/hooks/useSetting';
import { useGetSubscription } from '@/hooks/useSubscription';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { useAuth } from '@/state/auth';
import { RiskStatus } from '@/types';
import { getAvailableStatuses } from '@/utils/availableStatuses.util';
import { omit } from '@/utils/lodash.util';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

interface PDFExportFormValues {
  title: string;
  clientName: string;
  target: string;
  draft: boolean;
  version: string;
  statusFilter: string[];
  retest: boolean;
  sharedOutput: boolean;
  executiveSummaryPath: string; // "" = don't include
  narrativesPath: string; // "" = don't include
  appendixPath: string; // "" = don't include
  exportFormat: ExportFormatType;
  groupBy: string;
}

const getTodayISO = (): string => new Date().toISOString().split('T')[0];

export interface ExportPDFReportProps {
  /** Pre-selected risk keys from the vulnerabilities table (optional) */
  selectedRows?: string[];
  /**
   * Externally controlled open state. When provided together with
   * `onOpenChange`, the component renders no trigger button and delegates
   * open/close control to the parent.
   */
  open?: boolean;
  /**
   * Called when the modal requests to close (or when validation passes and
   * the export starts). Required when `open` is provided.
   */
  onOpenChange?: (open: boolean) => void;
  /** Pre-set export format when opened from an external dropdown. */
  initialExportFormat?: ExportFormatType;
  /** When true, default includeNarratives and includeAppendix to false, overriding saved settings. */
  skipMarkdownDefaults?: boolean;
}

/**
 * ExportPDFReport — "Export PDF" button + modal form for Praetorian users.
 *
 * Validates then generates a PDF pentest report via the orator backend.
 * Form values persist to localStorage between uses.
 *
 * Supports two modes:
 * - **Self-contained** (default): Renders its own trigger button.
 * - **Externally controlled**: When `open` + `onOpenChange` are provided, no
 *   button is rendered and the parent drives the modal's open state.
 */
export function ExportPDFReport({
  selectedRows = [],
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  initialExportFormat,
  skipMarkdownDefaults,
}: ExportPDFReportProps) {
  const isExternallyControlled =
    externalOpen !== undefined && externalOnOpenChange !== undefined;

  const { me, friend, isPraetorianUser } = useAuth();
  const customerEmail = friend || me;

  const { mode } = useVulnerabilityStatusMode();
  const availableStatuses = useMemo(
    () =>
      getAvailableStatuses(isPraetorianUser, mode).filter(
        s => s !== RiskStatus.Deleted
      ),
    [isPraetorianUser, mode]
  );

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isExternallyControlled ? externalOpen : internalOpen;
  const setOpen = isExternallyControlled
    ? externalOnOpenChange
    : setInternalOpen;

  const [validationErrors, setValidationErrors] = useState<
    ValidateReportError[]
  >([]);
  const [missingFiles, setMissingFiles] = useState<string[]>([]);

  // Fetch org settings to pre-populate client name
  const { data: settings, status: settingsStatus } = useMy(
    { resource: 'setting' },
    { email: customerEmail, staleTime: 30000, gcTime: 300000 }
  );

  // Fetch available files to populate variant dropdowns
  const { data: files = [] } = useMy(
    { resource: 'file' },
    { email: customerEmail }
  );

  const organizationName = useMemo(() => {
    const setting = settings?.find(
      s => s.name === ORGANIZATION_DISPLAY_NAME_KEY
    );
    return typeof setting?.value === 'string' ? setting.value : '';
  }, [settings]);

  // Fetch subscription to pre-populate dates and customer domain
  const { data: subscriptionData, status: subscriptionStatus } =
    useGetSubscription({ email: customerEmail });

  // Build variant file lists for each report section
  const fileVariants = useMemo(() => {
    const esVariants = files
      .filter(
        f =>
          f.name === 'executive_summary.md' ||
          f.name.match(/^reports\/executive_summary.*\.md$/)
      )
      .map(f => f.name);
    const narrVariants = files
      .filter(
        f =>
          f.name === 'narratives.md' ||
          f.name.match(/^reports\/narratives.*\.md$/)
      )
      .map(f => f.name);
    const appVariants = files
      .filter(
        f =>
          f.name === 'appendix.md' || f.name.match(/^reports\/appendix.*\.md$/)
      )
      .map(f => f.name);

    // Ensure root primary is first if present
    const sortPrimary = (variants: string[], primary: string) => {
      const sorted = variants.filter(v => v !== primary);
      if (variants.includes(primary)) sorted.unshift(primary);
      return sorted;
    };

    return {
      executiveSummary: sortPrimary(esVariants, 'executive_summary.md'),
      narratives: sortPrimary(narrVariants, 'narratives.md'),
      appendix: sortPrimary(appVariants, 'appendix.md'),
    };
  }, [files]);

  // Compute default form values from fetched data
  const defaultFormValues = useMemo((): PDFExportFormValues => {
    return {
      title: 'Penetration Test Report',
      clientName: organizationName,
      target: 'Environment',
      draft: true,
      retest: false,
      version: '',
      statusFilter: [RiskStatus.Opened],
      sharedOutput: false,
      executiveSummaryPath: 'executive_summary.md',
      narrativesPath: 'narratives.md',
      appendixPath: 'appendix.md',
      exportFormat: ExportFormat.PDF,
      groupBy: 'attack_surface',
    };
  }, [organizationName]);

  // Customer-wide PDF export settings from the backend.
  const savedExportOptions = useMemo(() => {
    const setting = settings?.find(s => s.name === 'pdf-export-options');
    return setting?.value as PDFExportFormValues | undefined;
  }, [settings]);

  // User's form edits for the current modal session, layered on top of saved settings.
  const [userOverrides, setUserOverrides] = useState<
    Partial<PDFExportFormValues>
  >({});
  const [prevOpen, setPrevOpen] = useState(false);

  // Detect open/close transitions during render (React 19 pattern — no effects needed).
  if (open && !prevOpen) {
    setPrevOpen(true);
    const overrides: Partial<PDFExportFormValues> = {};
    if (initialExportFormat) overrides.exportFormat = initialExportFormat;
    if (skipMarkdownDefaults) {
      overrides.narrativesPath = '';
      overrides.appendixPath = '';
    }
    setUserOverrides(overrides);
  }
  if (!open && prevOpen) {
    setPrevOpen(false);
  }

  const { mutate: saveExportSettings } = useUpdateSetting(customerEmail);

  const { mutateAsync: validateReport, status: validateStatus } =
    useValidateReport();
  const { mutateAsync: exportReport } = useExportReport();

  const isValidating = validateStatus === 'pending';
  const isDataLoading =
    settingsStatus === 'pending' || subscriptionStatus === 'pending';

  const current: PDFExportFormValues = {
    ...defaultFormValues,
    ...(savedExportOptions ?? {}),
    ...userOverrides,
  };

  const persistFormValues = () => {
    const rest = omit(current, 'includeNarratives', 'includeAppendix');
    saveExportSettings({
      name: 'pdf-export-options',
      value: isExternallyControlled ? rest : current,
      silent: true,
    });
  };

  if (!isPraetorianUser) {
    return null;
  }

  function handleFieldChange<K extends keyof PDFExportFormValues>(
    field: K,
    value: PDFExportFormValues[K]
  ) {
    setUserOverrides(prev => ({ ...prev, [field]: value }));
  }

  async function handleGenerate() {
    setValidationErrors([]);
    setMissingFiles([]);

    try {
      const result = await validateReport({
        risk_keys: selectedRows.length > 0 ? selectedRows : undefined,
        status_filter: current.statusFilter,
        customer_email: customerEmail,
        executive_summary_path: current.executiveSummaryPath || undefined,
        narratives_path: current.narrativesPath || undefined,
        group_by: current.groupBy,
      });

      if (!result.valid) {
        setMissingFiles([]);
        const definitionErrors = result.errors.filter(
          e => e.type === 'missing_definition'
        );
        const phaseTagErrors = result.errors.filter(
          e => e.type === 'missing_phase_tags'
        );
        setValidationErrors([...definitionErrors, ...phaseTagErrors]);
        return;
      }

      // Validation passed — start export and close modal
      persistFormValues();
      setOpen(false);
      await exportReport({
        risk_keys: selectedRows.length > 0 ? selectedRows : undefined,
        status_filter: current.statusFilter,
        config: {
          title: current.title,
          client_name: current.clientName,
          target: current.target,
          start_date: subscriptionData?.startDate ?? '',
          end_date: subscriptionData?.endDate ?? '',
          report_date: getTodayISO(),
          draft: current.draft,
          retest: current.retest,
          version: current.version,
        },
        shared_output: current.sharedOutput,
        customer_email: customerEmail,
        executive_summary_path: current.executiveSummaryPath || undefined,
        narratives_path: current.narrativesPath || undefined,
        appendix_path: current.appendixPath || undefined,
        export_format: current.exportFormat,
        group_by: current.groupBy,
      });
    } catch {
      // Error toast is handled by useExportReport / useValidateReport
    }
  }

  const hasValidationErrors =
    validationErrors.length > 0 || missingFiles.length > 0;

  return (
    <>
      {!isExternallyControlled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setValidationErrors([]);
            setMissingFiles([]);
            setOpen(true);
          }}
          data-testid="export-pdf-button"
          aria-label="Export PDF report"
          className="rounded-full"
        >
          <DocumentArrowDownIcon className="size-4" />
          Export PDF
        </Button>
      )}

      <Modal
        open={open}
        onClose={() => {
          persistFormValues();
          setOpen(false);
        }}
        hideClose
        title={
          <h2 className="py-2 text-lg font-bold">
            {current.exportFormat === ExportFormat.ZIP
              ? 'ZIP Report Export'
              : 'PDF Report Export'}
          </h2>
        }
        className="scrollbar-dark border-chariot-stroke border-2 border-x-0 p-0"
        footer={{
          text: isValidating
            ? 'Validating...'
            : current.exportFormat === ExportFormat.ZIP
              ? 'Generate ZIP'
              : 'Generate PDF',
          isLoading: isValidating,
          disabled: isValidating,
          onClick: handleGenerate,
        }}
      >
        <div className="scrollbar-dark max-h-[600px] overflow-auto px-6 py-4">
          {isDataLoading ? (
            <>
              <Loader isLoading className="mb-4 h-[60px] w-full" />
              <Loader isLoading className="mb-4 h-[60px] w-full" />
              <Loader isLoading className="h-[60px] w-full" />
            </>
          ) : (
            <div className="flex flex-col gap-5">
              {/* Selected rows notice */}
              {selectedRows.length > 0 && (
                <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border px-4 py-3 text-sm">
                  Export limited to {selectedRows.length} selected{' '}
                  {selectedRows.length === 1
                    ? 'vulnerability'
                    : 'vulnerabilities'}
                </div>
              )}

              {/* Validation errors */}
              {hasValidationErrors && (
                <div className="border-chariot-status-critical bg-chariot-status-critical/5 rounded-md border px-4 py-3">
                  <p className="text-chariot-status-critical mb-2 text-sm font-semibold">
                    Validation errors — please resolve before generating:
                  </p>
                  {missingFiles.length > 0 && (
                    <div>
                      <p className="text-chariot-text-primary mb-1 text-sm">
                        The following files were not found:
                      </p>
                      <ul className="list-disc pl-5 text-sm">
                        {missingFiles.map(f => (
                          <li key={f} className="text-chariot-text-secondary">
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {validationErrors.some(
                    e => e.type === 'missing_definition'
                  ) && (
                    <div>
                      <p className="text-chariot-text-primary mb-1 text-sm">
                        Missing definitions for the following risks:
                      </p>
                      <ul className="list-disc pl-5 text-sm">
                        {validationErrors
                          .filter(e => e.type === 'missing_definition')
                          .map(err => (
                            <li
                              key={err.risk_key}
                              className="text-chariot-text-secondary"
                            >
                              {err.risk_name}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {validationErrors.some(
                    e => e.type === 'missing_phase_tags'
                  ) && (
                    <div>
                      <p className="text-chariot-text-primary mb-1 text-sm">
                        The following findings are missing phase tags. Add a{' '}
                        <code className="text-xs">phase_*</code> tag to each
                        before exporting with tag-based grouping:
                      </p>
                      <ul className="list-disc pl-5 text-sm">
                        {validationErrors
                          .filter(e => e.type === 'missing_phase_tags')
                          .map(err => (
                            <li
                              key={err.risk_key}
                              className="text-chariot-text-secondary"
                            >
                              {err.risk_name}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Finding Status Filter — hidden when specific rows are selected */}
              {selectedRows.length === 0 && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">
                    Finding Status Filter
                  </label>
                  <div className="flex flex-col">
                    {availableStatuses.map(status => (
                      <div
                        key={status}
                        className="border-chariot-stroke flex items-center gap-3 border-b py-2.5 last:border-b-0"
                      >
                        <Checkbox
                          name={`status-${status}`}
                          label={getStatusLabel(status, mode)}
                          value={
                            current.statusFilter.includes(status)
                              ? 'true'
                              : 'false'
                          }
                          onChange={() => {
                            const currentFilter = current.statusFilter;
                            if (currentFilter.includes(status)) {
                              handleFieldChange(
                                'statusFilter',
                                currentFilter.filter(s => s !== status)
                              );
                            } else {
                              handleFieldChange('statusFilter', [
                                ...currentFilter,
                                status,
                              ]);
                            }
                          }}
                          showTick
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Group Findings By */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Group Findings By</label>
                <select
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                  value={current.groupBy}
                  onChange={e => handleFieldChange('groupBy', e.target.value)}
                >
                  <option value="attack_surface">Attack Surface</option>
                  <option value="tag">Tags (Phase)</option>
                </select>
              </div>

              {/* Report Title */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Report Title</label>
                <input
                  type="text"
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                  value={current.title}
                  onChange={e => handleFieldChange('title', e.target.value)}
                  placeholder="Client Penetration Test"
                />
              </div>

              {/* Client Name + Target (side by side) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Client Name</label>
                  <input
                    type="text"
                    className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                    value={current.clientName}
                    onChange={e =>
                      handleFieldChange('clientName', e.target.value)
                    }
                    placeholder="Client Inc"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Target</label>
                  <input
                    type="text"
                    className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                    value={current.target}
                    onChange={e => handleFieldChange('target', e.target.value)}
                    placeholder="example.com"
                  />
                </div>
              </div>

              {/* Draft checkbox */}
              <div className="flex flex-col">
                <div className="flex items-center gap-3 py-2.5">
                  <Checkbox
                    name="draft"
                    label="Draft"
                    value={current.draft ? 'true' : 'false'}
                    onChange={() => handleFieldChange('draft', !current.draft)}
                    showTick
                  />
                  <span className="text-chariot-text-secondary text-xs">
                    Adds a DRAFT watermark to the generated report
                  </span>
                </div>
                <div className="flex items-center gap-3 py-2.5">
                  <Checkbox
                    name="retest"
                    label="Retest"
                    value={current.retest ? 'true' : 'false'}
                    onChange={() =>
                      handleFieldChange('retest', !current.retest)
                    }
                    showTick
                  />
                  <span className="text-chariot-text-secondary text-xs">
                    Include retest status badges and sections in the report
                  </span>
                </div>
              </div>

              {/* Executive Summary file select */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Executive Summary</label>
                <select
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                  value={current.executiveSummaryPath}
                  onChange={e =>
                    handleFieldChange('executiveSummaryPath', e.target.value)
                  }
                >
                  <option value="">(None)</option>
                  {fileVariants.executiveSummary.map(f => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              {/* Narratives file select */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Narratives</label>
                <select
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                  value={current.narrativesPath}
                  onChange={e =>
                    handleFieldChange('narrativesPath', e.target.value)
                  }
                >
                  <option value="">(None)</option>
                  {fileVariants.narratives.map(f => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              {/* Appendix file select */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Appendix</label>
                <select
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                  value={current.appendixPath}
                  onChange={e =>
                    handleFieldChange('appendixPath', e.target.value)
                  }
                >
                  <option value="">(None)</option>
                  {fileVariants.appendix.map(f => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              {/* Version */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Version</label>
                <input
                  type="text"
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden"
                  value={current.version}
                  onChange={e => handleFieldChange('version', e.target.value)}
                  placeholder="1.0"
                />
              </div>

              {/* Save to Shared Files toggle */}
              <div className="border-chariot-stroke border-t pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium">Save to Shared Files</p>
                    <p className="text-chariot-text-secondary text-xs">
                      Copy the generated PDF to the customer&apos;s shared files
                      so they can view it in Customer Manager
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={current.sharedOutput}
                    onChange={checked =>
                      handleFieldChange('sharedOutput', checked)
                    }
                    ariaLabel="Save to shared files"
                    data-testid="shared-output-toggle"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
